import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import {
  ArrowLeft,
  Calendar,
  ListChecks,
  User,
  CheckCircle,
  XCircle,
  Hourglass,
} from "lucide-react";
import { createClient } from "@supabase/supabase-js";

const supabase = createClient(
  import.meta.env.VITE_SUPABASE_URL!,
  import.meta.env.VITE_SUPABASE_ANON_KEY!
);

type CheckIn = {
  id: string;
  created_at: string;
  desafio_id: number;
  foto_url?: string;
  desafios: {
    id: number;
    nome: string;
    data_inicio: string;
    data_fim: string;
  };
};

type DesafiosAgrupados = Record<
  string,
  {
    nome: string;
    data_inicio: string;
    data_fim: string;
    foto_url?: string;
    registros: string[];
  }
>;

const formatDate = (dateStr: string) => {
  const parts = dateStr.split("T")[0].split("-");
  const safeDate = new Date(
    Number(parts[0]),
    Number(parts[1]) - 1,
    Number(parts[2]),
    12
  );
  return safeDate.toLocaleDateString("pt-BR");
};

function getAllDatesBetween(start: string, end: string): string[] {
  const startDate = new Date(start);
  const endDate = new Date(end);
  const dates: string[] = [];

  for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
    const dateCopy = new Date(d);
    dates.push(dateCopy.toISOString().split("T")[0]);
  }

  return dates;
}

export default function MyCheckIns() {
  const { user } = useAuth();
  const [checkins, setCheckins] = useState<CheckIn[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCheckIns = async () => {
      if (!user?.cpf) return;

      setIsLoading(true);
      // console.log("USER...:", user);
      try {
        const { data, error } = await supabase
          .from("checkins")
          .select(
            `
              id,
              created_at,
              desafio_id,
              foto_url,
              desafios (
                id,
                nome,
                data_inicio,
                data_fim
              )
            `
          )
          .eq("usuario_id", user.id);

        if (error) throw error;

        setCheckins((data || []) as CheckIn[]);
        setHasSearched(true);
      } catch (error) {
        console.error(error);
        toast({
          title: "Erro na consulta",
          description:
            "Não foi possível consultar os check-ins. Tente novamente.",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchCheckIns();
  }, [user]);

  const desafiosAgrupados: DesafiosAgrupados = checkins.reduce(
    (acc, checkin) => {
      const desafio = checkin.desafios;
      const id = desafio.id.toString();

      if (!acc[id]) {
        acc[id] = {
          nome: desafio.nome,
          data_inicio: desafio.data_inicio,
          data_fim: desafio.data_fim,
          foto_url: desafio.foto_url,
          registros: [],
        };
      }

      acc[id].registros.push(checkin.created_at);
      return acc;
    },
    {} as DesafiosAgrupados
  );

  return (
    <div className="min-h-screen bg-gradient-hero p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex justify-center mb-4">
          <img
            src="/pele-bonita.png"
            alt="Campanha Pele Bonita"
            className="mx-auto my-3 w-28 max-w-sm h-auto object-contain rounded-b-lg"
          />
        </div>

        <div className="flex items-center gap-4 mb-8">
          <Button
            variant="outline"
            size="icon"
            className="bg-white/10 border-white/20 text-white hover:bg-white/20"
            onClick={() => navigate("/checkin")}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-white">Meus Check-ins</h1>
            <p className="text-white/80">
              Consulte o histórico dos seus check-ins nos desafios
            </p>
          </div>
        </div>

        {/* Resultados */}
        <Card className="backdrop-blur-sm bg-white/95 border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ListChecks className="h-5 w-5" />
              Histórico de Check-ins
            </CardTitle>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <User className="w-4 h-4" />
              <span>Nome: {user?.nome}</span>
            </div>
            {/* <CardDescription>
              {isLoading
                ? "Consultando check-ins..."
                : checkins.length > 0
                ? `${checkins.length} check-ins encontrados`
                : hasSearched
                ? "Nenhum check-in encontrado para este CPF"
                : ""}
            </CardDescription> */}
          </CardHeader>
          <CardContent>
            {checkins.length > 0 ? (
              <div className="space-y-6">
                <div className="space-y-6">
                  {Object.entries(desafiosAgrupados).map(([id, desafio]) => {
                    const dias = getAllDatesBetween(
                      desafio.data_inicio,
                      desafio.data_fim
                    );

                    return (
                      <div key={id}>
                        <h2 className="text-xl font-bold mb-2">
                          {desafio.nome}{" "}
                          <span className="text-sm font-medium text-muted-foreground">
                            ({desafio.registros.length} de {dias.length} dias
                            concluídos)
                          </span>
                        </h2>

                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                          {dias.map((dia, index) => {
                            // dia já está em "YYYY-MM-DD"
                            const hojeStr = new Date().toLocaleDateString(
                              "en-CA"
                            ); // YYYY-MM-DD no fuso local

                            // Verificar se já houve check-in nesse dia, ignorando a hora
                            const fezCheckIn = desafio.registros.some((r) => {
                              const dataCheckIn = new Date(r);
                              const dataCheckInStr = dataCheckIn
                                .toISOString()
                                .split("T")[0];

                              return dataCheckInStr === dia;
                            });

                            const status =
                              dia > hojeStr
                                ? "futuro"
                                : dia === hojeStr
                                ? fezCheckIn
                                  ? "feito"
                                  : "aguardando"
                                : fezCheckIn
                                ? "feito"
                                : "faltou";

                            const isPrimeiro = index === 0;
                            const isUltimo = index === dias.length - 1;

                            const bgColor =
                              status === "feito"
                                ? "bg-green-50"
                                : status === "faltou"
                                ? "bg-red-50"
                                : "bg-yellow-50";

                            return (
                              <div
                                key={dia}
                                className={`flex flex-col justify-between items-start p-3 rounded-md border shadow-sm ${bgColor} min-h-[110px]`}
                              >
                                <div className="flex items-center gap-1 text-sm font-medium">
                                  <Calendar className="h-4 w-4" />
                                  {formatDate(dia)}
                                </div>

                                {isPrimeiro && (
                                  <span className="text-blue-600 text-xs font-semibold mt-1">
                                    Início do desafio
                                  </span>
                                )}
                                {isUltimo && (
                                  <span className="text-rose-600 text-xs font-semibold mt-1">
                                    Fim do desafio
                                  </span>
                                )}

                                <div className="mt-2 flex items-center gap-1 text-sm">
                                  {status === "feito" ? (
                                    <>
                                      <CheckCircle className="h-4 w-4 text-green-600" />
                                      <span className="text-green-700 text-xs">
                                        Feito
                                      </span>
                                    </>
                                  ) : status === "faltou" ? (
                                    <>
                                      <XCircle className="h-4 w-4 text-red-500" />
                                      <span className="text-red-600 text-xs">
                                        Faltou
                                      </span>
                                    </>
                                  ) : (
                                    <>
                                      <Hourglass className="h-4 w-4 text-yellow-600" />
                                      <span className="text-yellow-700 text-xs">
                                        Aguardando
                                      </span>
                                    </>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              !isLoading &&
              hasSearched && (
                <div className="text-center py-12">
                  <User className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">
                    Ainda não há check-ins registrados para este CPF.
                  </p>
                  <p className="text-sm text-muted-foreground mt-2">
                    Faça seu primeiro check-in para começar a participar dos
                    desafios!
                  </p>
                  <Button
                    className="mt-4"
                    variant="outline"
                    onClick={() => navigate("/checkin")}
                  >
                    Fazer Check-in
                  </Button>
                </div>
              )
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
